import { useEffect, useState } from "react";
import type { GoalsMap, MonthlyRow } from "@/components/teams/metrics-tracker-table";
import { MetricsTrackerTable } from "@/components/teams/metrics-tracker-table";

/** Percent-based metrics stored as 0–100 in owner_metric_target — convert to 0..1 ratios. */
const PERCENT_GOAL_KEYS = new Set(["closeRate", "errorRate", "grossProfitMargin"]);

/**
 * Builds the goals map from the nr_ targets configured for the Non
 * Relational Channel Champion. Opportunities, closed deals, gross sales,
 * net sales, average order value, close rate, error rate, and gross profit
 * margin (net sales as a % of gross sales) have configured goals.
 */
function buildGoals(rawGoals: Record<string, number>): GoalsMap {
  const goals: GoalsMap = {};
  if (rawGoals.opportunities !== undefined) {
    goals.opportunities = { value: rawGoals.opportunities, comparison: "gte" };
  }
  if (rawGoals.closedDeals !== undefined) {
    goals.closedDeals = { value: rawGoals.closedDeals, comparison: "gte" };
  }
  if (rawGoals.grossSales !== undefined) {
    goals.grossSales = { value: rawGoals.grossSales, comparison: "gte" };
  }
  if (rawGoals.netSales !== undefined) {
    goals.netSales = { value: rawGoals.netSales, comparison: "gte" };
  }
  if (rawGoals.avgOrderValue !== undefined) {
    goals.avgOrderValue = { value: rawGoals.avgOrderValue, comparison: "gte" };
  }
  if (rawGoals.closeRate !== undefined) {
    goals.closeRate = {
      value: PERCENT_GOAL_KEYS.has("closeRate") ? rawGoals.closeRate / 100 : rawGoals.closeRate,
      comparison: "gte",
    };
  }
  if (rawGoals.errorRate !== undefined) {
    goals.errorRate = {
      value: PERCENT_GOAL_KEYS.has("errorRate") ? rawGoals.errorRate / 100 : rawGoals.errorRate,
      comparison: "lte",
    };
  }
  if (rawGoals.grossProfitMargin !== undefined) {
    goals.grossProfitMargin = {
      value: PERCENT_GOAL_KEYS.has("grossProfitMargin")
        ? rawGoals.grossProfitMargin / 100
        : rawGoals.grossProfitMargin,
      comparison: "gte",
    };
  }
  return goals;
}

export function FpNonRelationalMetricsChart() {
  const [rows, setRows] = useState<MonthlyRow[] | null>(null);
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const [goals, setGoals] = useState<GoalsMap>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/fp-consultants/non-relational/monthly-metrics");
        if (!res.ok) {
          throw new Error(`Failed to load metrics (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setRows(data.monthly ?? []);
          setFiscalYear(data.fiscalYear ?? null);
          setGoals(buildGoals(data.goals ?? {}));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load metrics");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground">Loading Non Relational channel metrics…</div>
    );
  }

  if (error) {
    return <div className="text-sm text-destructive">{error}</div>;
  }

  if (!rows || rows.length === 0) {
    return <div className="text-sm text-muted-foreground">No monthly metrics found yet.</div>;
  }

  return (
    <MetricsTrackerTable
      rows={rows}
      fiscalYear={fiscalYear}
      goals={goals}
      title="Non Relational Lead Channels Monthly Metrics"
      subtitle="Non Relational Lead Channels"
    />
  );
}
